import React, { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import api, { formatError } from "../lib/api";

const SERVICES = ["Architecture", "3D Elevation", "Floor Plan", "Interior Design", "Construction", "Turnkey Project", "Renovation", "Other"];

function getUTM() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
  };
}

export default function LeadForm({ sourcePage = "", sourceProject = "", extra = {}, dark = false, defaultService = "", onDone }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", service: defaultService, plot_size: "", message: "", honeypot: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    if (!form.name.trim() || form.phone.trim().length < 7) {
      setError("Please enter your name and a valid mobile number.");
      setStatus("error");
      return;
    }
    setStatus("loading"); setError("");
    try {
      await api.post("/leads", { ...form, ...getUTM(), ...extra, source_page: sourcePage || window.location.pathname, source_project: sourceProject });
      setStatus("done");
      if (onDone) setTimeout(onDone, 1600);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Could not submit. Please try again.");
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div data-testid="lead-success" className={`rounded-xl2 p-8 text-center ${dark ? "bg-white/5 text-white" : "bg-beige"}`}>
        <CheckCircle2 size={44} className="mx-auto text-gold mb-3" />
        <h3 className="text-xl font-bold mb-2">Thank you!</h3>
        <p className={dark ? "text-white/70" : "text-muted"}>Your enquiry has been received. Our team will contact you shortly.</p>
      </div>
    );
  }

  const inputCls = `w-full rounded-xl border px-4 py-3 text-[15px] outline-none transition-colors ${dark ? "bg-white/5 border-white/15 text-white placeholder-white/40 focus:border-gold" : "bg-white border-line text-ink placeholder-muted/60 focus:border-gold"}`;

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-4">
      <input type="text" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={set("honeypot")} className="hidden" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="lead-name" className={inputCls} placeholder="Full Name *" value={form.name} onChange={set("name")} required />
        <input data-testid="lead-phone" className={inputCls} placeholder="Mobile Number *" type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} required />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="lead-email" className={inputCls} placeholder="Email" type="email" inputMode="email" value={form.email} onChange={set("email")} />
        <input data-testid="lead-city" className={inputCls} placeholder="City" value={form.city} onChange={set("city")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <select data-testid="lead-service" className={inputCls} value={form.service} onChange={set("service")}>
          <option value="">Select Service</option>
          {SERVICES.map((s) => <option key={s} value={s} className="text-ink">{s}</option>)}
        </select>
        <input data-testid="lead-plot" className={inputCls} placeholder="Plot Size (e.g. 30x50)" value={form.plot_size} onChange={set("plot_size")} />
      </div>
      <textarea data-testid="lead-message" className={inputCls} rows={4} placeholder="Tell us about your project..." value={form.message} onChange={set("message")} />
      {status === "error" && <p data-testid="lead-error" className="text-sm text-red-500">{error}</p>}
      <button type="submit" data-testid="lead-submit" disabled={status === "loading"} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-white font-semibold py-3.5 shadow-gold hover:bg-gold-dark transition-colors disabled:opacity-70">
        {status === "loading" ? <><Loader2 size={18} className="animate-spin" /> Submitting...</> : "Send Enquiry"}
      </button>
    </form>
  );
}
